import { isTerminalOutcome, isWindowGated, type Channel, type InquiryType, type Outcome } from "./types";
import { ghostThresholdMs } from "./ghostLabel";
import type { NurtureHoldReason } from "./nurtureHold";
import { registeredOutboundText } from "./outboundText";
import { HOURS, STANDARD_WINDOW_MS, windowState } from "./window";

/**
 * The follow-up nudge (Agent Brains §5.5, apps/web/src/lib/agents.ts),
 * charter verbatim: mission "Nothing rots. Sleeping value wakes at the right
 * moment." Never: "Never nudges out of window on Meta rails. Never overrides
 * the frequency cap." Handoff: "Owns per-lead clocks. Segment-wide waves
 * belong to Campaigns." Produces: "A per-lead call list and timed nudges."
 *
 * This file builds the "timed nudges" half of that charter, per thread. The
 * "call list" half is deliberately NOT built here: the charter says sleeping
 * VALUE wakes, and nothing in this system yet scores which sleeping thread is
 * worth a human's phone call rather than a machine's nudge. That is exactly
 * the outcome-history-volume gap the prior `blockedBy` line named, and
 * inventing a scoring formula now would be inventing spec (section 12). See
 * FINDINGS.md TA-181.
 *
 * Modeled directly on `ghostLabel.ts`'s split, which this file leans on
 * rather than re-deriving: a loose SQL prefilter in
 * `packages/agents/src/followUpSweep.ts` narrows candidates using the
 * shortest threshold below, and this pure function makes the actual call, so
 * there is exactly one definition of "gone quiet" and one of "gone silent too
 * long to still be ours" (the latter is `ghostThresholdMs`, imported rather
 * than copied).
 */

/**
 * How long a thread sits quiet, after WE spoke last, before it is worth a
 * nudge.
 *
 * SPEC GAP: unlike `GHOST_AFTER_MS` (Composer 4.5's spec'd 21/10 days), the
 * vault's §5.5 charter names the mission and the two things this agent must
 * never do, but no number for "sleeping" at all. There is no vault figure to
 * transcribe here.
 *
 * Chosen conservatively, the direction section 0 asks for when unsure: fire
 * too early and this reads as pestering a customer who has not gone quiet by
 * any real standard; fire too late and there is no runway left before the
 * thread auto-ghosts at `GHOST_AFTER_MS` / `GHOST_AFTER_REPAIR_MS`. Three days
 * (two for repairs, which ghost in ten rather than twenty-one) leaves roughly
 * two and a half weeks and one week of runway respectively, comfortably short
 * of the ghost line, matching the requirement that a nudge fire before a
 * thread ghosts, never after.
 *
 * AWAITING RATIFICATION (TA-181), the same status `HOLDING_MESSAGE`
 * (draftSchema.ts) and `DIGEST_LIST_CAP` (outcomeDigest.ts) carry: an
 * implementer's conservative placeholder, not a number Mike or Kabir has
 * seen. Tune only once real outcome history exists to tune it against (F-15's
 * n>=5 bar): the point of the `blockedBy` line this build leaves in
 * `agents.ts`.
 */
export const FOLLOWUP_SILENCE_MS = 3 * 24 * HOURS;

/**
 * Repairs move on a faster clock, same reasoning as `GHOST_AFTER_REPAIR_MS`.
 * Same provisional, AWAITING RATIFICATION status as `FOLLOWUP_SILENCE_MS`.
 */
export const FOLLOWUP_SILENCE_REPAIR_MS = 2 * 24 * HOURS;

/**
 * The shortest threshold either arm can apply, and therefore the only safe
 * SQL prefilter bound. Same reasoning as `GHOST_EARLIEST_MS`: a query
 * narrowed on the standard figure would never show this function a repair
 * that is only two days quiet.
 */
export const FOLLOWUP_SILENCE_EARLIEST_MS = Math.min(
  FOLLOWUP_SILENCE_MS,
  FOLLOWUP_SILENCE_REPAIR_MS,
);

/** Mirrors `ghostThresholdMs`: unknown gets the LONGER wait, never the shorter. */
export function followUpThresholdMs(inquiryType: InquiryType | string | null): number {
  return inquiryType === "repair" ? FOLLOWUP_SILENCE_REPAIR_MS : FOLLOWUP_SILENCE_MS;
}

export type FollowUpKind = "check_in_nudge";

export type FollowUpSkipReason =
  | "terminal_outcome"
  | "frozen"
  | "mid_escalation"
  | "no_outbound_yet"
  | "customer_spoke_last"
  | "owed_answer"
  | "clock_skew"
  | "not_enough_silence"
  | "past_ghost_threshold"
  | "nudged_recently"
  | NurtureHoldReason;

export type FollowUpDecision =
  | { readonly due: true; readonly kind: FollowUpKind }
  | { readonly due: false; readonly reason: FollowUpSkipReason };

export interface FollowUpInput {
  readonly lastOutboundAt: Date | null;
  readonly lastCustomerMessageAt: Date | null;
  /** The latest UNREVERSED outcome on the thread, or null. Same shape as `GhostInput.outcome`. */
  readonly outcome: Outcome | string | null;
  readonly inquiryType: InquiryType | string | null;
  /** An open row in `thread_freezes` (the injection freeze, Agent Brains section 12). T3, human-cleared only. */
  readonly isFrozen: boolean;
  /**
   * A `pending_approval` draft sits on this thread: a human has not yet
   * answered a customer who is waiting RIGHT NOW. That is the escalation
   * SLA's territory (Agent Brains §12), not a sleeping lead. The §5.5 handoff
   * line is explicit that Follow-ups owns per-lead clocks, not active
   * conversations.
   */
  readonly hasPendingApprovalDraft: boolean;
  /**
   * Loop f item OWED. The SHOP owes this customer an answer: the last shop
   * turn was a holding line ("checking on that, back shortly") or the agent
   * did not know and the owner must answer, so the customer is waiting on us
   * and a "wanted to check back in" would read as the shop forgetting its own
   * promise (Agent Brains section 10.1, the Follow-up agent forgetting what the
   * customer is waiting for). Derived by `shopOwedAnswer` from the last shop
   * turn's provenance. True also when that provenance cannot be read: nothing
   * is nudged on a guess.
   */
  readonly shopOwesAnswer: boolean;
  /**
   * A follow-up nudge already went out for THIS silence episode (keyed on
   * `lastOutboundAt` by the sweep; see `followUpSweep.ts`). Prevents a nudge
   * on every sweep tick for a thread that is still quiet. A fresh outbound
   * message starts a new episode and this resets to false.
   */
  readonly alreadyNudgedThisEpisode: boolean;
  /**
   * TA-1361 (CLAUDE.md section 3, B13): the thread's latest route is class D,
   * or its contact holds an open complaint (`nurtureHoldFor`, nurtureHold.ts),
   * read by the sweep. Null when neither holds. Required, so a caller cannot
   * forget it and nudge a complaint thread.
   */
  readonly nurtureHold: NurtureHoldReason | null;
  readonly now: Date;
}

export function planFollowUp(input: FollowUpInput): FollowUpDecision {
  // A label the owner tapped, or the ghost auto-label, settles the account.
  // `booked`, `deposit` and `ongoing` (the enum's word for unknown) settle
  // nothing and do not block a nudge: the deal is still open, or undecided,
  // and undecided is exactly the "sleeping" this agent exists to wake.
  if (isTerminalOutcome(input.outcome)) {
    return { due: false, reason: "terminal_outcome" };
  }

  if (input.isFrozen) return { due: false, reason: "frozen" };
  if (input.hasPendingApprovalDraft) return { due: false, reason: "mid_escalation" };

  const out = input.lastOutboundAt;
  if (out === null) return { due: false, reason: "no_outbound_yet" };

  // They answered. Whatever this thread needs, it is not a wake-up: it is a
  // reply we owe, which is a live conversation the moment a draft exists for
  // it, not a sleeping lead. A tie goes to the customer, same reading
  // `isGhosted` gives a tie.
  const back = input.lastCustomerMessageAt;
  if (back !== null && back.getTime() >= out.getTime()) {
    return { due: false, reason: "customer_spoke_last" };
  }

  // We owe them. Decided before any clock, so silence of any length (too
  // short, skewed, past the ghost line, already nudged) never turns a promise
  // we have not kept into a nudge. Terminal outcomes and the freeze above
  // still win: a closed or frozen thread is nobody's to answer here.
  if (input.shopOwesAnswer) return { due: false, reason: "owed_answer" };

  const elapsed = input.now.getTime() - out.getTime();
  // Clock skew fails toward silence, the same direction `isGhosted` and
  // `needsOutcomeTag` fail: a nudge nobody can explain is worse than one that
  // fires a day late.
  if (elapsed < 0) return { due: false, reason: "clock_skew" };

  if (elapsed < followUpThresholdMs(input.inquiryType)) {
    return { due: false, reason: "not_enough_silence" };
  }

  // Past the ghost line, this thread is the ghost sweep's business, not
  // ours. Nudging a thread that is about to (or already did) get labeled
  // `ghosted` is the "fire after it ghosts" order the charter forbids, and
  // the outcome check above cannot always catch it: the ghost sweep runs on
  // its own hourly cadence and a thread can cross the line before that sweep
  // has caught up. One definition of the ghost line, reused rather than
  // re-derived, so the two can never quietly disagree about where it sits.
  if (elapsed >= ghostThresholdMs(input.inquiryType)) {
    return { due: false, reason: "past_ghost_threshold" };
  }

  if (input.alreadyNudgedThisEpisode) {
    return { due: false, reason: "nudged_recently" };
  }

  // TA-1361 (CLAUDE.md section 3, B13): a class D thread, or a contact holding
  // an open complaint, is a person's, never a nudge's. Decided last, so the
  // hold's restraint row names a nudge that was due and held, never a thread
  // that was not due anyway.
  if (input.nurtureHold !== null) return { due: false, reason: input.nurtureHold };

  return { due: true, kind: "check_in_nudge" };
}

/**
 * Loop f item OWED. The registered fixed lines that tell a customer "we will
 * come back to you": the escalation holding line, the reply agent's fixed
 * draft (the holding line, with the disclosure or the contact ask joined), the
 * outage acknowledgement and the freeze acknowledgement. Ids into the outbound
 * text registry (outboundText.ts), never a match on customer text. A line
 * added to the registry that promises an answer is added here by hand; the
 * test beside this file pins that every id still names a fixed entry.
 *
 * The contact ask alone is NOT here: it asks the customer something, so the
 * customer owes the next turn.
 *
 * AWAITING RATIFICATION (loop f item OWED): which registered lines count as "we owe you an answer", and holding a thread whose last shop turn cannot be read, are implementer readings of Kabir's 2026-10-03 go on the follow-up fix, not wording he has seen.
 */
export const OWES_ANSWER_TEXT_IDS: readonly string[] = Object.freeze([
  "escalation.holding",
  "reply.holding_draft",
  "reply.outage_ack",
  "reply.freeze_ack",
]);

/**
 * Every exact body those ids may go out as, for a SQL `= ANY(...)` against the
 * body a draft was sent as. An id the registry no longer holds as a fixed text
 * contributes nothing here and fails the pin test, so a rename is loud.
 */
export function owesAnswerRenderings(): string[] {
  const out = new Set<string>();
  for (const id of OWES_ANSWER_TEXT_IDS) {
    const entry = registeredOutboundText(id);
    if (entry?.form !== "fixed") continue;
    for (const r of entry.renderings) out.add(r);
  }
  return [...out];
}

/**
 * Why the shop owes an answer, as a code and never text (custody: codes and
 * counts only). `provenance_unreadable` is the deny-by-default arm. The two
 * `approved_*` codes are loop h item OWED2 (TA-579, ULTRA's guard): the
 * promise read from the owner's own release of a draft, for draft mode, where
 * Mike hand-sends the line and no transmission row exists. `rejected_draft`
 * is TA-610 (decision 174): the approver rejected the draft for the customer's
 * turn and nothing has gone out since, so the customer is still owed an answer.
 */
export type OwedAnswerCode =
  | "holding_line"
  | "needs_owner_knowledge"
  | "registered_holding_text"
  | "holding_sent_after"
  | "approved_holding_line"
  | "approved_needs_owner_knowledge"
  | "rejected_draft"
  | "provenance_unreadable";

/**
 * The thread's newest owner-released draft (status approved, sent or
 * transmitted) created at or after the customer's last message. Flags only.
 */
export interface ReleasedDraftFacts {
  /** `drafts.holding_line`: true for the fixed holding line, null for not recorded. */
  readonly holdingLine: boolean | null;
  /** `drafts.needs_owner_knowledge`: the KB could not answer and the owner must (A3.6). */
  readonly needsOwnerKnowledge: boolean;
  /**
   * A shop or agent message went out after the owner released it that is not
   * the hand-send echo: the shop has said something else since, so the draft
   * no longer stands as the last word. The echo (the released text after
   * `normalizeEchoText`, or the first owner-typed message after the release in
   * its Meta window, rows 157 and 158) does not count; the read is
   * `releasedDraftAnsweredSince` in textEcho.ts.
   */
  readonly answeredSince: boolean;
}

/**
 * TA-610 (decision 174): the thread's newest rejected draft created at or
 * after the customer's last turn. A rejection sends nothing, so the customer
 * who wrote is still owed an answer until the shop says something.
 */
export interface RejectedDraftFacts {
  /**
   * A shop or agent message went out at or after the customer's turn: the
   * customer heard from the shop in this exchange (the owner typed in the
   * inbox, or another draft of the exchange was sent), so the rejection left
   * nobody unanswered. Whether what went out was itself a promise is read by
   * the arms above, never here.
   */
  readonly answeredSince: boolean;
  /**
   * OWEDECHO fix round: this rejected draft was created after the newest
   * owner-released draft (false when there is none). An owed rejection that
   * is newer is the current promise, so it decides ahead of the release and
   * keys its own episode; an older release would key an episode whose pages
   * already fired, and the new one would never page.
   */
  readonly newerThanReleased: boolean;
}

/**
 * What the sweep read about the thread's LAST SHOP TURN (the latest `shop` or
 * `agent` message). Facts and flags only; no body is carried here.
 */
export interface LastShopTurnFacts {
  /** The sender of the last shop turn (`shop` = the owner typed it, `agent` = the system sent a draft), or null when the thread has no such message row. */
  readonly sender: string | null;
  /** The turn is an `agent` message and the draft it was transmitted from was found. */
  readonly draftFound: boolean;
  /** `drafts.holding_line` of that draft: true for the fixed holding line, null for not recorded. */
  readonly draftHoldingLine: boolean | null;
  /** `drafts.needs_owner_knowledge`: the KB could not answer and the owner must (A3.6). */
  readonly draftNeedsOwnerKnowledge: boolean | null;
  /** The draft's sent body is exactly a rendering of a registered line in `OWES_ANSWER_TEXT_IDS`. */
  readonly textIsRegisteredHolding: boolean;
  /** An `escalation.holding_sent` event on the thread is newer than the last shop turn and not older than the customer's last message: a holding line went out after it and nothing has answered since. */
  readonly holdingSentAfter: boolean;
  /**
   * TA-579: the last shop turn belongs to the current exchange: the customer
   * never wrote, or the thread's last outbound or the turn itself is not older
   * than the customer's last message. False means the turn answered an earlier
   * message, so its draft and its sender say nothing about this one.
   */
  readonly turnIsCurrent: boolean;
  /** TA-579: `threads.last_outbound_at` is set. No shop turn and nothing ever out means nobody promised anything. */
  readonly outboundRecorded: boolean;
  /** TA-579 (ULTRA's guard): the newest owner-released draft of this exchange, or null when there is none. */
  readonly approvedDraft: ReleasedDraftFacts | null;
  /** TA-610 (decision 174): the newest rejected draft of this exchange, or null when there is none. */
  readonly rejectedDraft: RejectedDraftFacts | null;
}

export type OwedAnswer =
  | { readonly owed: false }
  | { readonly owed: true; readonly code: OwedAnswerCode };

/**
 * Does the shop owe this customer an answer? Pure; the sweeps read the facts
 * through one shared fragment (packages/db/src/owedAnswer.ts), so the follow-up
 * nudge, the campaign wave and the ghost label cannot read it three ways.
 *
 * Order: a holding line sent after the last turn is the real last turn, so it
 * decides first. Then the draft, when one is found under the current turn's
 * platform id (whatever the sender). Then the owner's release of a draft
 * (TA-579): in draft mode the owner hand-sends an approved holding line, so no
 * transmission names it and the echo reads as an owner-typed turn; the release
 * is the evidence. Then a rejected draft of this exchange with nothing sent
 * since (TA-610, decision 174): the approver said no and nobody answered, so
 * the customer is owed one, last among the promises so a released draft or a
 * holding line keeps its own code. Then the provenance: a turn from an
 * earlier exchange says nothing about this one (not owed: nobody has promised
 * anything yet); a thread with no shop turn and nothing ever sent has
 * promised nothing; an owner-typed turn is a person answering (not owed); an
 * agent turn is read from its draft; anything the sweep could not read (no shop message row on a thread something
 * went out on, an agent turn with no draft row, a sender it does not know) is
 * owed, since a nudge nobody can justify is worse than one that never fires
 * (CLAUDE.md section 0, deny by default).
 */
export function shopOwedAnswer(f: LastShopTurnFacts): OwedAnswer {
  if (f.holdingSentAfter) return { owed: true, code: "holding_sent_after" };
  // A found draft speaks whatever the sender: recordEcho files our own echo as
  // 'shop' when it cannot pair it with one claim, while transmit still settles
  // the claim under the same platform id. The draft is the evidence.
  if (f.turnIsCurrent && f.draftFound) {
    if (f.draftHoldingLine === true) return { owed: true, code: "holding_line" };
    if (f.draftNeedsOwnerKnowledge === true) return { owed: true, code: "needs_owner_knowledge" };
    if (f.textIsRegisteredHolding) return { owed: true, code: "registered_holding_text" };
  }
  // TA-610: ahead of the provenance arms, which read a rejected-and-silent
  // exchange as a stale turn (not owed) or an unreadable one (no page).
  const rejectedOwed = f.rejectedDraft !== null && !f.rejectedDraft.answeredSince;
  // OWEDECHO fix round: a release no longer ends at the customer's next turn
  // (row 158), so an older release can still stand when a newer draft is
  // rejected. The newer rejection decides first; an answered one falls
  // through so the release still holds.
  if (rejectedOwed && f.rejectedDraft!.newerThanReleased) return { owed: true, code: "rejected_draft" };
  const released = f.approvedDraft;
  if (released !== null && !released.answeredSince) {
    if (released.holdingLine === true) return { owed: true, code: "approved_holding_line" };
    if (released.needsOwnerKnowledge) return { owed: true, code: "approved_needs_owner_knowledge" };
  }
  if (rejectedOwed) return { owed: true, code: "rejected_draft" };
  if (!f.turnIsCurrent) return { owed: false };
  if (f.sender === null && !f.outboundRecorded) return { owed: false };
  if (f.sender === "shop") return { owed: false };
  if (f.sender !== "agent" || !f.draftFound) return { owed: true, code: "provenance_unreadable" };
  return { owed: false };
}

/**
 * TA-579: how long after a Meta window closes an owed thread may still page
 * `owed_answer_closed`. The page fires on the first follow-up tick after the
 * close (the worker runs that sweep every minute); the horizon only bounds how
 * late a missed close is still raised, after a worker outage or on the first
 * deploy, so the deploy does not page every owed thread that ever closed.
 *
 * AWAITING RATIFICATION (loop h item OWED2): one standard window (24h) past the close is an implementer reading, not a figure Kabir has seen.
 */
export const OWED_CLOSED_PAGE_HORIZON_MS = STANDARD_WINDOW_MS;

export type OwedPageKind = "owed_answer_grace" | "owed_answer_closed";

export type OwedPageSkip =
  | "not_window_gated"
  | "no_customer_message"
  | "terminal_outcome"
  | "frozen"
  | "pending_draft"
  | "not_owed"
  | "in_window"
  | "closed_past_horizon";

export interface OwedPageInput {
  readonly channel: Channel;
  readonly lastCustomerMessageAt: Date | null;
  /** `shopOwedAnswer(...).owed` for the thread, read through the shared fragment. */
  readonly owed: boolean;
  readonly terminalOutcome: boolean;
  readonly isFrozen: boolean;
  readonly hasPendingApprovalDraft: boolean;
  readonly now: Date;
}

export type OwedPageDecision =
  | { readonly page: false; readonly reason: OwedPageSkip }
  | { readonly page: true; readonly kind: OwedPageKind; readonly windowState: "grace" | "closed" };

/**
 * TA-579 (03c-50, ULTRA's decision sweep): a page at 24h always lands after
 * the window has closed, when nothing can be sent freeform any more. So an
 * owed thread on a Meta rail pages once when its window enters grace (the last
 * two hours, `windowState`), while the owner can still answer, and once more
 * when it closes with still no answer (CLAUDE.md F-01/F-02: a loud owner alert
 * after close). Both are pages, never amber. The window logic is
 * packages/core/src/window.ts, read here and never re-derived.
 *
 * No page on a thread with a draft waiting: the escalation sweep owns that one
 * (`escalation_window_closed`), and a second page would say the same thing
 * twice. No page on a frozen thread or one with a terminal outcome, the same
 * exclusions `planFollowUp` makes. Not owed, no page: an unanswered customer
 * with no promise made is the reply pipeline's, not this page's.
 */
export function planOwedPage(input: OwedPageInput): OwedPageDecision {
  if (!isWindowGated(input.channel)) return { page: false, reason: "not_window_gated" };
  if (input.lastCustomerMessageAt === null) return { page: false, reason: "no_customer_message" };
  if (input.terminalOutcome) return { page: false, reason: "terminal_outcome" };
  if (input.isFrozen) return { page: false, reason: "frozen" };
  if (input.hasPendingApprovalDraft) return { page: false, reason: "pending_draft" };
  if (!input.owed) return { page: false, reason: "not_owed" };
  const state = windowState({ lastCustomerMessageAt: input.lastCustomerMessageAt, now: input.now });
  if (state === "in_window") return { page: false, reason: "in_window" };
  if (state === "grace") return { page: true, kind: "owed_answer_grace", windowState: "grace" };
  const sinceClose = input.now.getTime() - input.lastCustomerMessageAt.getTime() - STANDARD_WINDOW_MS;
  if (sinceClose >= OWED_CLOSED_PAGE_HORIZON_MS) return { page: false, reason: "closed_past_horizon" };
  return { page: true, kind: "owed_answer_closed", windowState: "closed" };
}
